/**
 * Client for the Guests tab (#1013): the board read, the Start / End / Reopen / Reschedule lifecycle, a
 * guest's reschedule, Add guests, and the event's activity log. Everything is under `/api/events`; a refusal
 * is `detail = {code, message}` (read it with `lib/apiErrors.errorText` and `lifecycleCode`).
 *
 * The browser sends an `operation_id` with every write so a retry replays instead of repeating. Moves,
 * check-in and removal go through `lib/eventContactsApi` (`transitionContact`, `removeAttendee`).
 */
import axios from 'axios';

const API = () => `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const enc = encodeURIComponent;

const cleanParams = (params = {}) => Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '' && value !== false),
);

/** The `code` of a refusal (`event_already_started`, `version_conflict`, `contact_exists`…), or null. */
export function lifecycleCode(err) {
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === 'object') return detail.code ?? detail.error ?? null;
    return null;
}

export function createEventGuestsApi(getAuthHeader) {
    const cfg = (extra = {}) => ({ headers: getAuthHeader ? getAuthHeader() : {}, ...extra });
    const event = (eventId) => `${API()}/events/${enc(eventId)}`;
    const data = (res) => res.data;
    return {
        /** `{event, cards, groups, columns, summary, capacity, workflow, owner_options, filters}` */
        getBoard: (eventId, params) => axios.get(`${event(eventId)}/outreach/board`, cfg({ params: cleanParams(params) })).then(data),

        /** Lifecycle. Start answers the event; End `{event, moved_to_no_show, attended, unconfirmed}`; Reopen `{event, reverted}`. */
        startEvent: (eventId, operationId) => axios.post(`${event(eventId)}/start`, { operation_id: operationId }, cfg()).then(data),
        endEvent: (eventId, operationId) => axios.post(`${event(eventId)}/end`, { operation_id: operationId }, cfg()).then(data),
        reopenEvent: (eventId, operationId) => axios.post(`${event(eventId)}/reopen`, { operation_id: operationId }, cfg()).then(data),
        /** `{going_without_check_in, attended, unconfirmed}`: what End would do, from the selector End itself uses. */
        getEndPreview: (eventId) => axios.get(`${event(eventId)}/end-preview`, cfg()).then(data),

        /** `body`: `{date, start_time?, end_time?, reconfirm, expected_updated_at, operation_id?}`. Answers `{event, reconfirmed}`. */
        rescheduleEvent: (eventId, body) => axios.post(`${event(eventId)}/reschedule`, body, cfg()).then(data),
        /** `body`: `{target_event_id, expected_state_version, operation_id}`. Answers `{source, target}`. */
        rescheduleGuest: (eventId, contactId, body) => axios.post(`${event(eventId)}/contacts/${enc(contactId)}/reschedule`, body, cfg()).then(data),
        /** Events a guest can move to: `GET /events` from today, narrowed by the caller. */
        listUpcomingEvents: (params) => axios.get(`${API()}/events`, cfg({ params: cleanParams(params) })).then(data),

        /** Add existing contacts: `{contact_ids, start, walk_in?, assign_to_self?, include_outcomes: true, operation_id}`. */
        addGuests: (eventId, body) => axios.post(`${event(eventId)}/attendees`, body, cfg()).then(data),
        /**
         * Import CSV (D12): `{rows: [{email, first_name?, last_name?, company?, title?, phone?}], start, operation_id}`, 1-500 rows.
         * Answers `{added, created, already_guests, skipped: [{row, email, reason}]}`; `row` is the 1-based row of the rows sent.
         */
        importGuests: (eventId, body) => axios.post(`${event(eventId)}/attendees/import`, body, cfg()).then(data),
        /** Create the contact first (dedupe, plan limit, audit and webhook all live there): a duplicate email is 409 `contact_exists`. */
        createContact: (payload) => axios.post(`${API()}/contacts`, payload, cfg()).then(data),
        searchContacts: (params) => axios.get(`${API()}/contacts`, cfg({ params: cleanParams(params) })).then(data),
        /** `{data: [{contact_id, journey_label|stage_label}]}` for the contacts already on the event. */
        getContactStates: (eventId, contactIds) => axios.get(`${event(eventId)}/contact-states`, cfg({ params: { contact_ids: contactIds.join(',') } })).then(data),
        listContactTags: () => axios.get(`${API()}/tags`, cfg({ params: { entity: 'contact' } })).then(data),

        /**
         * The event's activity log: `{items, next_cursor, counts_by_kind}`. `params`: `kind`, `actor_id`
         * (`system` = automatic), `source` (`manual|auto`), `contact_id`, `q`, `cursor`, `limit` (≤100).
         */
        getHistory: (eventId, params) => axios.get(`${event(eventId)}/history`, cfg({ params: cleanParams(params) })).then(data),
    };
}

export default createEventGuestsApi;
